import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import CloudinaryUploadWidget from '../../../../app/components/CloudinaryUploadWidget';
import { CHALLENGE_CONFIGS, UploadField, UrlField, CheckboxField, URL_PATTERNS, SubmissionField } from './challengeConfigs';

interface Props {
  challengeSlug: string;
  classId: string;
}

const getCsrf = () => JSON.parse(document.getElementById('csrf_token')!.innerHTML);

const makeSnapshot = (fields: {
  textTitle: string; textBody: string; teamName: string;
  studentNames: string; mentorName: string; dobFilter: string; dobGroupDisplay: string;
  urlInputs: Record<string, string>;
  fileUrls: Record<string, string>;
}) => JSON.stringify(fields);

const fieldLabel = (field: SubmissionField, t: (k: string) => string) => {
  if ('fieldKey' in field) return (field as UrlField).label ?? t(`gallery.uploadType.${field.type}`);
  return (field as UploadField).label ?? t(`gallery.uploadType.${field.type}`);
};


const inputProps = (isApproved: boolean) => ({
  size: 'sm' as const,
  borderColor: 'gray.300',
  bg: isApproved ? 'gray.50' : 'white',
  isReadOnly: isApproved,
  _hover: isApproved ? {} : { borderColor: 'blue.400' },
});

const ChallengeSubmissionForm = ({ challengeSlug, classId }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const config = CHALLENGE_CONFIGS[challengeSlug];

  const [textTitle, setTextTitle] = useState('');
  const [textBody, setTextBody] = useState('');
  const [teamName, setTeamName] = useState('');
  const [studentNames, setStudentNames] = useState('');
  const [mentorName, setMentorName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [dobFilter, setDobFilter] = useState('');
  const [dobGroupDisplay, setDobGroupDisplay] = useState('');

  const [uploadedFiles, setUploadedFiles] = useState<Record<string, { url: string; filename: string }>>({});
  const [urlInputs, setUrlInputs] = useState<Record<string, string>>({});
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});

  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [error, setError] = useState('');

  const primarySlug =
    (config?.submissions.find((f): f is UploadField => 'slug' in f))?.slug ?? challengeSlug;

  useEffect(() => {
    const params: Record<string, string> = { slug: challengeSlug, class_id: classId };
    if (primarySlug !== challengeSlug) params.upload_slug = primarySlug;
    axios
      .get('/api/v1/gallery/submissions/', { params })
      .then((r) => {
        const data = r.data;
        setSchoolName(data.schoolName || '');

        if (data.submission) {
          setTextTitle(data.submission.textTitle);
          setTextBody(data.submission.textBody);
          setTeamName(data.submission.teamName);
          setStudentNames(data.submission.studentNames);
          setMentorName(data.submission.mentorName);
          setDobFilter(data.submission.dobFilter);
          setDobGroupDisplay(data.submission.dobGroupDisplay);
          setIsApproved(data.submission.isApproved);
          const urlFields = data.submission.urlFields || {};
          const fileUrls = data.submission.fileUrls || {};
          if (data.submission.urlFields) setUrlInputs(urlFields);
          if (data.submission.fileUrls) {
            const restored: Record<string, { url: string; filename: string }> = {};
            for (const [slug, url] of Object.entries(fileUrls as Record<string, string>)) {
              restored[slug] = { url, filename: '' };
            }
            setUploadedFiles(restored);
          }
          setSavedSnapshot(makeSnapshot({
            textTitle: data.submission.textTitle,
            textBody: data.submission.textBody,
            teamName: data.submission.teamName,
            studentNames: data.submission.studentNames,
            mentorName: data.submission.mentorName,
            dobFilter: data.submission.dobFilter,
            dobGroupDisplay: data.submission.dobGroupDisplay,
            urlInputs: urlFields,
            fileUrls: fileUrls as Record<string, string>,
          }));
          setSubmitted(true);
        } else {
          setStudentNames(data.studentNames);
          setMentorName(data.mentorName);
          setTeamName(data.studentNames);
        }

        if (data.fileUrl && !uploadedFiles[primarySlug]) {
          setUploadedFiles((prev) => ({
            ...prev,
            [primarySlug]: { url: data.fileUrl, filename: data.filename || '' },
          }));
        }
      })
      .catch(() => {});
  }, [challengeSlug, classId]);

  const saveFile = (slug: string, url: string, filename: string) => {
    axios
      .post(
        '/api/v1/syllabus/user_file_upload/',
        { slug, class_id: classId, file_url: url, filename },
        { headers: { 'X-CSRFToken': getCsrf() } },
      )
      .catch((e) => console.error(e));
  };

  const handleUpload = (slug: string, url: string, filename: string) => {
    setUploadedFiles((prev) => ({ ...prev, [slug]: { url, filename } }));
    saveFile(slug, url, filename);
  };

  const handleDeleteUpload = (slug: string) => {
    axios
      .delete('/api/v1/syllabus/user_file_upload/', {
        params: { class_id: classId, slug },
        headers: { 'X-CSRFToken': getCsrf() },
      })
      .then(() => {
        setUploadedFiles((prev) => {
          const next = { ...prev };
          delete next[slug];
          return next;
        });
      })
      .catch((e) => console.error(e));
  };

  const isFilled = (field: SubmissionField) => {
    if (field.type === 'checkbox') return !!checkboxes[(field as CheckboxField).fieldKey];
    if ('fieldKey' in field) return !!urlInputs[(field as UrlField).fieldKey];
    return !!(uploadedFiles[(field as UploadField).slug]?.url);
  };

  const hasRequiredUploads = (() => {
    if (!config) return false;
    const ungroupedOk = config.submissions.filter((f) => !f.group && !('optional' in f && (f as UrlField).optional)).every(isFilled);
    const groups = [...new Set(config.submissions.map((f) => f.group).filter(Boolean))] as string[];
    const groupedOk = groups.every((g) => config.submissions.filter((f) => f.group === g).some(isFilled));
    const effectiveErrors = Object.keys(urlErrors).filter((fieldKey) => {
      const field = config.submissions.find(
        (f) => 'fieldKey' in f && (f as UrlField).fieldKey === fieldKey
      ) as UrlField | undefined;
      if (!field?.group) return true;
      return !config.submissions.filter((f) => f.group === field.group).some(isFilled);
    });
    return ungroupedOk && groupedOk && effectiveErrors.length === 0;
  })();

  const currentSnapshot = makeSnapshot({
    textTitle, textBody, teamName, studentNames, mentorName, dobFilter, dobGroupDisplay,
    urlInputs,
    fileUrls: Object.fromEntries(Object.entries(uploadedFiles).map(([slug, f]) => [slug, f.url])),
  });
  const hasChanges = savedSnapshot === null || currentSnapshot !== savedSnapshot;

  const validateUrl = (fieldKey: string, type: UrlField['type'], value: string) => {
    const rule = URL_PATTERNS[type];
    if (!rule || !value) {
      setUrlErrors((prev) => { const n = { ...prev }; delete n[fieldKey]; return n; });
      return;
    }
    if (!rule.pattern.test(value)) {
      setUrlErrors((prev) => ({ ...prev, [fieldKey]: rule.hint }));
    } else {
      setUrlErrors((prev) => { const n = { ...prev }; delete n[fieldKey]; return n; });
    }
  };

  const handleSubmit = () => {
    setError('');
    setSubmitting(true);
    axios
      .post(
        '/api/v1/gallery/submissions/',
        {
          slug: challengeSlug,
          ...(primarySlug !== challengeSlug ? { upload_slug: primarySlug } : {}),
          class_id: classId,
          textTitle,
          textBody,
          teamName,
          studentNames,
          mentorName,
          dobFilter,
          dobGroupDisplay,
          urlFields: urlInputs,
          fileUrls: Object.fromEntries(
            Object.entries(uploadedFiles).map(([slug, f]) => [slug, f.url])
          ),
        },
        { headers: { 'X-CSRFToken': getCsrf() } },
      )
      .then(() => {
        const currentFileUrls = Object.fromEntries(
          Object.entries(uploadedFiles).map(([slug, f]) => [slug, f.url])
        );
        setSavedSnapshot(makeSnapshot({
          textTitle, textBody, teamName, studentNames, mentorName, dobFilter, dobGroupDisplay,
          urlInputs,
          fileUrls: currentFileUrls,
        }));
        setSubmitted(true);
        toast({
          title: t('gallery.toastSuccessTitle'),
          description: t('gallery.toastSuccessDesc'),
          status: 'success',
          duration: 6000,
          isClosable: true,
          position: 'top',
        });
      })
      .catch((e) => setError(e.response?.data?.error || t('gallery.error')))
      .finally(() => setSubmitting(false));
  };

  if (!config) return null;

  // Pre-process submissions into render items — grouped fields rendered together
  type RenderItem =
    | { kind: 'field'; field: SubmissionField }
    | { kind: 'group'; group: string; fields: SubmissionField[] };

  const renderItems: RenderItem[] = [];
  const seenGroups = new Set<string>();
  for (const field of config.submissions) {
    if (!field.group) {
      renderItems.push({ kind: 'field', field });
    } else if (!seenGroups.has(field.group)) {
      seenGroups.add(field.group);
      renderItems.push({
        kind: 'group',
        group: field.group,
        fields: config.submissions.filter((f) => f.group === field.group),
      });
    }
  }

  const renderSingleField = (field: SubmissionField, isGrouped: boolean) => {
    if (field.type === 'checkbox') {
      const cbField = field as CheckboxField;
      return (
        <FormControl isRequired={!isGrouped}>
          <Checkbox
            isChecked={isApproved || !!checkboxes[cbField.fieldKey]}
            isDisabled={isApproved}
            colorScheme="blue"
            alignItems="baseline"
            onChange={(e) => setCheckboxes((prev) => ({ ...prev, [cbField.fieldKey]: e.target.checked }))}
          >
            <Text fontSize="sm" color="gray.700" lineHeight="short">
              {cbField.label ?? cbField.fieldKey}
              {!isGrouped && <Text as="span" color="red.500" ml={1}>*</Text>}
            </Text>
          </Checkbox>
        </FormControl>
      );
    }

    if ('fieldKey' in field) {
      const urlField = field as UrlField;
      return (
        <FormControl isRequired={!isGrouped && !urlField.optional} isInvalid={!!urlErrors[urlField.fieldKey]}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            {fieldLabel(field, t)}
          </FormLabel>
          <Input
            {...inputProps(isApproved)}
            type="url"
            value={urlInputs[urlField.fieldKey] || ''}
            onChange={(e) => {
              const val = e.target.value;
              setUrlInputs((prev) => ({ ...prev, [urlField.fieldKey]: val }));
              validateUrl(urlField.fieldKey, urlField.type, val);
            }}
            placeholder="https://"
          />
          {urlErrors[urlField.fieldKey] && (
            <Text fontSize="xs" color="red.500" mt={1}>{urlErrors[urlField.fieldKey]}</Text>
          )}
        </FormControl>
      );
    }

    const uploadField = field as UploadField;
    const existing = uploadedFiles[uploadField.slug];
    return (
      <FormControl isRequired={!isGrouped}>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          {fieldLabel(field, t)}
        </FormLabel>
        <CloudinaryUploadWidget
          isImageOnly={field.type === 'image'}
          resourceType={field.type === 'image' ? undefined : 'raw'}
          clientAllowedFormats={field.type === 'txt' ? ['txt'] : uploadField.acceptedFormats}
          initialUrl={existing?.url}
          initialFilename={existing?.filename}
          isViewOnly={isApproved}
          compact
          onImageUpload={isApproved ? undefined : ({ url, original_filename }) =>
            handleUpload(uploadField.slug, url, original_filename)
          }
          onDelete={isApproved ? undefined : () => handleDeleteUpload(uploadField.slug)}
        />
      </FormControl>
    );
  };

  const SectionLabel = ({ label }: { label: string }) => (
    <Box borderLeft="3px solid" borderColor="blue.500" pl={3} mb={1}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
        {label}
      </Text>
    </Box>
  );

  return (
    <Box mt={6} borderRadius="xl" overflow="hidden" boxShadow="sm" border="1px solid" borderColor="gray.200">
      {/* Header */}
      <Box bg="blue.600" px={6} py={5}>
        <Text color="white" fontWeight="bold" fontSize="lg" lineHeight="short">
          {t('gallery.submitTitle')}
        </Text>
        <Text color="blue.200" fontSize="sm" mt={1} fontWeight="medium">
          {config.name}
        </Text>
      </Box>

      {/* Status bar */}
      {isApproved && (
        <Box bg="green.50" borderBottom="1px solid" borderColor="green.100" px={6} py={3}>
          <HStack spacing={2} alignItems="baseline">
            <Box w={2} h={2} borderRadius="full" bg="green.400" flexShrink={0} />
            <Text fontSize="sm" color="green.700" fontWeight="medium">{t('gallery.approved')}</Text>
          </HStack>
        </Box>
      )}
      {!isApproved && submitted && (
        <Box bg="blue.50" borderBottom="1px solid" borderColor="blue.100" px={6} py={3}>
          <HStack spacing={2} alignItems="baseline">
            <Box w={2} h={2} borderRadius="full" bg="blue.400" flexShrink={0} />
            <Text fontSize="sm" color="blue.700" fontWeight="medium">{t('gallery.pendingReview')}</Text>
          </HStack>
        </Box>
      )}

      <Box bg="white" px={6} py={6}>
        <Stack spacing={6}>

          {/* Section: basic info */}
          <Stack spacing={4}>
            <SectionLabel label={t('gallery.sectionBasicInfo')} />

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.projectTitle')}</FormLabel>
              <Input {...inputProps(isApproved)} value={textTitle} onChange={(e) => setTextTitle(e.target.value)} maxLength={120} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.description')}</FormLabel>
              <Textarea
                {...inputProps(isApproved)}
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                maxLength={1500}
                rows={4}
                placeholder={t('gallery.descriptionPlaceholder')}
                resize="vertical"
              />
              <Text fontSize="xs" color={textBody.length >= 1400 ? 'orange.500' : 'gray.400'} textAlign="right" mt={1}>
                {textBody.length}/1500
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.teamName')}</FormLabel>
              <Input {...inputProps(isApproved)} value={teamName} onChange={(e) => setTeamName(e.target.value)} maxLength={200} />
            </FormControl>

            {schoolName && (
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.schoolName')}</FormLabel>
                <Input size="sm" value={schoolName} isReadOnly bg="gray.50" borderColor="gray.200" _focus={{ boxShadow: 'none' }} />
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.studentNames')}</FormLabel>
              <Input {...inputProps(isApproved)} value={studentNames} onChange={(e) => setStudentNames(e.target.value)} maxLength={500} />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.mentorName')}</FormLabel>
              <Input {...inputProps(isApproved)} value={mentorName} onChange={(e) => setMentorName(e.target.value)} maxLength={200} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.schoolLevel')}</FormLabel>
              <RadioGroup
                value={dobFilter}
                onChange={(val) => { setDobFilter(val); setDobGroupDisplay(''); }}
                isDisabled={isApproved}
              >
                <HStack spacing={6} alignItems="baseline">
                  <Radio value="os" size="sm" colorScheme="blue" alignItems="baseline">{t('gallery.primarySchool')}</Radio>
                  <Radio value="ss" size="sm" colorScheme="blue" alignItems="baseline">{t('gallery.secondarySchool')}</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {dobFilter && (
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">{t('gallery.gradeGroup')}</FormLabel>
                <Input
                  {...inputProps(isApproved)}
                  value={dobGroupDisplay}
                  onChange={(e) => setDobGroupDisplay(e.target.value)}
                  maxLength={100}
                  placeholder={t('gallery.gradeGroupPlaceholder')}
                />
              </FormControl>
            )}
          </Stack>

          <Divider borderColor="gray.200" />

          {/* Section: submission */}
          <Stack spacing={4}>
            <SectionLabel label={t('gallery.sectionSubmission')} />

            {renderItems.map((item, i) => {
              if (item.kind === 'field') {
                return <Box key={i}>{renderSingleField(item.field, false)}</Box>;
              }
              return (
                <Box key={i} bg="blue.50" borderRadius="lg" p={4} border="1px solid" borderColor="blue.100">
                  <Text fontSize="xs" fontWeight="semibold" color="blue.500" mb={3} textTransform="uppercase" letterSpacing="wide">
                    {t('gallery.chooseOne')}
                  </Text>
                  <Stack spacing={0}>
                    {item.fields.map((f, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && (
                          <HStack my={3} spacing={3}>
                            <Divider borderColor="blue.200" />
                            <Text fontSize="xs" color="blue.400" fontWeight="semibold" whiteSpace="nowrap">
                              {t('gallery.or')}
                            </Text>
                            <Divider borderColor="blue.200" />
                          </HStack>
                        )}
                        {renderSingleField(f, true)}
                      </React.Fragment>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          {/* Action */}
          {!isApproved && (
            <Stack spacing={2} pt={1}>
              {error && (
                <Text color="red.500" fontSize="sm">{error}</Text>
              )}
              <Box>
                <Button
                  colorScheme="blue"
                  size="md"
                  isLoading={submitting}
                  onClick={handleSubmit}
                  isDisabled={!hasRequiredUploads || (submitted && !hasChanges)}
                  px={8}
                  fontWeight="semibold"
                  borderRadius="lg"
                >
                  {submitted ? t('gallery.update') : t('gallery.submit')}
                </Button>
              </Box>
            </Stack>
          )}

        </Stack>
      </Box>
    </Box>
  );
};

export default ChallengeSubmissionForm;
